import React, { useState, useEffect, useCallback } from 'react';
import { partnersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Handshake, Plus, PencilSimple, Trash, Plugs, ChartBar,
  TrendUp, Warning, ShieldCheck,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend,
} from 'recharts';

const PARTNER_TYPES = [
  { value: 'delivery_partner', label: 'Delivery Partner' },
  { value: 'dine_in_partner', label: 'Dine-In Partner' },
  { value: 'direct_partner', label: 'Direct / Owned' },
  { value: 'pickup_partner', label: 'Pickup Partner' },
  { value: 'marketplace_partner', label: 'Marketplace' },
  { value: 'unknown', label: 'Unknown' },
];
const CHANNELS = ['online', 'dine_in', 'takeaway'];
const SUBCHANNELS = ['delivery', 'discovery', 'reservation', 'walk_in', 'counter', 'own_delivery', 'qr_ordering', 'kiosk', 'own_app', 'own_website'];
const FULFILLMENT_MODES = ['off_premise', 'on_premise', 'pickup'];
const COMMERCIAL_MODELS = ['commission', 'fixed_fee', 'subscription', 'owned'];
const CAPABILITIES = ['delivery', 'dine_in', 'takeaway', 'pickup', 'booking', 'listing', 'deal_discovery', 'loyalty', 'subscription', 'qr_ordering'];
const SOURCE_FIELDS = [
  { value: '', label: 'Any field' },
  { value: 'order_source', label: 'Order Source' },
  { value: 'channel_raw', label: 'Channel Raw' },
  { value: 'payment_mode', label: 'Payment Mode' },
];

const fmtCurrency = (v) => v != null ? `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '0';
const COLORS = ['#06B6D4', '#D4FF00', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#EC4899', '#6366F1'];

const EMPTY_PARTNER = { partner_id: '', partner_name: '', partner_type: '', channel: '', subchannel: '', capabilities: [], fulfillment_mode: '', commercial_model: '', status: 'active', metadata: {} };
const EMPTY_MAPPING = { source_value: '', source_field: '', mapped_partner_id: '', mapping_type: 'exact', confidence: 0.9, priority: 30 };

const PartnerAdminPage = () => {
  const [tab, setTab] = useState('registry');
  const [partners, setPartners] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Registry
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PARTNER });

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Mappings
  const [showMapForm, setShowMapForm] = useState(false);
  const [mapForm, setMapForm] = useState({ ...EMPTY_MAPPING });
  const [filterPartner, setFilterPartner] = useState('');

  // Analytics
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([partnersApi.list(), partnersApi.listMappings()]);
      setPartners(pRes.data);
      setMappings(mRes.data);
    } catch { toast.error('Failed to load partner data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const stored = JSON.parse(localStorage.getItem('1os_user_info') || '{}');
      let orgId = stored.org_id;
      if (!orgId) {
        const { data: me } = await import('../lib/api').then(m => m.default.get('/auth/me'));
        orgId = me.org_id;
      }
      const [sRes, tRes] = await Promise.all([
        partnersApi.getSummary({ org_id: orgId }),
        partnersApi.getTrends({ org_id: orgId }),
      ]);
      setSummary(sRes.data);
      setTrends(tRes.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'analytics') fetchAnalytics(); }, [tab, fetchAnalytics]);

  // ── Registry handlers ──
  const openCreate = () => { setForm({ ...EMPTY_PARTNER }); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...p, capabilities: p.capabilities || [] }); setEditingId(p.partner_id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const handleSave = async () => {
    if (!form.partner_id.trim()) { toast.error('Partner ID is required'); return; }
    try {
      if (editingId) {
        await partnersApi.update(editingId, form);
        toast.success('Partner updated');
      } else {
        await partnersApi.create({ ...form, partner_id: form.partner_id.trim().toLowerCase().replace(/\s+/g, '_') });
        toast.success('Partner created');
      }
      closeForm(); fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const toggleCap = (cap) => setForm(f => ({ ...f, capabilities: f.capabilities.includes(cap) ? f.capabilities.filter(c => c !== cap) : [...f.capabilities, cap] }));

  // ── Delete handlers ──
  const openDelete = async (p) => {
    setDeleteTarget(p);
    setDeleteConfirmText('');
    setDeleteReason('');
    setDeleteUsage(null);
    try {
      const { data } = await partnersApi.getUsage(p.partner_id);
      setDeleteUsage(data);
    } catch { setDeleteUsage({ transaction_count: -1, is_system_partner: false, deletable: false }); }
  };
  const closeDelete = () => { setDeleteTarget(null); setDeleteUsage(null); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const usage = deleteUsage;
    const inUse = (usage?.transaction_count || 0) + (usage?.item_count || 0) > 0;
    if (inUse && deleteConfirmText !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setDeleteLoading(true);
    try {
      await partnersApi.delete(deleteTarget.partner_id, {
        force: inUse, reason: deleteReason || 'Deleted from Partner Admin',
      });
      toast.success(`Partner "${deleteTarget.partner_name}" deleted`);
      closeDelete(); fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object') toast.error(detail.message || 'Delete failed');
      else toast.error(detail || 'Delete failed');
    } finally { setDeleteLoading(false); }
  };

  // ── Mapping handlers ──
  const openMapCreate = () => { setMapForm({ ...EMPTY_MAPPING }); setShowMapForm(true); };
  const closeMapForm = () => setShowMapForm(false);

  const handleMapSave = async () => {
    if (!mapForm.source_value.trim() || !mapForm.mapped_partner_id) { toast.error('Source value and partner required'); return; }
    try {
      await partnersApi.createMapping(mapForm);
      toast.success('Mapping rule created'); closeMapForm(); fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
  };

  const filteredMappings = filterPartner ? mappings.filter(m => m.mapped_partner_id === filterPartner) : mappings;

  // ── Analytics: build trend lines per partner ──
  const trendByDate = {};
  (trends?.trends || []).forEach(t => {
    if (!trendByDate[t.date]) trendByDate[t.date] = { date: t.date };
    trendByDate[t.date][t.partner_id] = t.net_sales;
  });
  const trendChartData = Object.values(trendByDate).sort((a, b) => a.date.localeCompare(b.date));
  const trendPartnerIds = [...new Set((trends?.trends || []).map(t => t.partner_id))];

  if (loading) return <div className="py-16 text-center text-zinc-500">Loading partner data...</div>;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']" data-testid="partner-admin-title">Partner Admin</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage demand-origin partners, mapping rules, and performance</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-1 w-fit" data-testid="partner-admin-tabs">
        {[
          { key: 'registry', label: 'Partner Registry', icon: Handshake },
          { key: 'mappings', label: 'Mapping Rules', icon: Plugs },
          { key: 'analytics', label: 'Analytics', icon: ChartBar },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`partner-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-colors ${tab === t.key ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── REGISTRY TAB ── */}
      {tab === 'registry' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 font-mono">{partners.length} partners</span>
            <Button onClick={openCreate} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-black gap-1.5" data-testid="add-partner-btn">
              <Plus size={14} /> Add Partner
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="partner-registry-grid">
            {partners.map(p => (
              <div key={p.partner_id} className="surface-base rounded-lg p-4 border border-white/5 hover:border-white/10 transition-colors" data-testid={`partner-card-${p.partner_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${p.status === 'active' ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                    <span className="text-sm font-medium text-zinc-200">{p.partner_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(p)} className="p-1 text-zinc-500 hover:text-white transition-colors" data-testid={`edit-partner-${p.partner_id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => openDelete(p)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-partner-${p.partner_id}`}><Trash size={14} /></button>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-zinc-500">ID</span><span className="font-mono text-zinc-400">{p.partner_id}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="text-zinc-300">{p.partner_type || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Channel</span><span className="text-zinc-300">{p.channel || '-'}</span></div>
                  {p.subchannel && <div className="flex justify-between"><span className="text-zinc-500">Subchannel</span><span className="text-zinc-300">{p.subchannel}</span></div>}
                  {p.fulfillment_mode && <div className="flex justify-between"><span className="text-zinc-500">Fulfillment</span><span className="text-zinc-300">{p.fulfillment_mode}</span></div>}
                  {p.commercial_model && <div className="flex justify-between"><span className="text-zinc-500">Commercial</span><span className="text-zinc-300">{p.commercial_model}</span></div>}
                </div>
                {p.capabilities?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.capabilities.map(c => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{c}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Create/Edit Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeForm}>
              <div className="bg-[#121214] rounded-lg border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="partner-form-modal">
                <h3 className="text-lg font-medium">{editingId ? 'Edit Partner' : 'New Partner'}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Partner ID</Label><Input value={form.partner_id} onChange={e => setForm(f => ({ ...f, partner_id: e.target.value }))} disabled={!!editingId} placeholder="e.g. eazydiner" className="bg-[#18181B] border-white/10 text-xs" data-testid="partner-form-id" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Partner Name</Label><Input value={form.partner_name} onChange={e => setForm(f => ({ ...f, partner_name: e.target.value }))} placeholder="e.g. EazyDiner" className="bg-[#18181B] border-white/10 text-xs" data-testid="partner-form-name" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Partner Type</Label>
                    <Select value={form.partner_type || '__NONE__'} onValueChange={v => setForm(f => ({ ...f, partner_type: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs" data-testid="partner-form-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select type</SelectItem>{PARTNER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Channel</Label>
                    <Select value={form.channel || '__NONE__'} onValueChange={v => setForm(f => ({ ...f, channel: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs" data-testid="partner-form-channel"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select channel</SelectItem>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Subchannel</Label>
                    <Select value={form.subchannel || '__NONE__'} onValueChange={v => setForm(f => ({ ...f, subchannel: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select</SelectItem>{SUBCHANNELS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Fulfillment</Label>
                    <Select value={form.fulfillment_mode || '__NONE__'} onValueChange={v => setForm(f => ({ ...f, fulfillment_mode: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select</SelectItem>{FULFILLMENT_MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Commercial Model</Label>
                    <Select value={form.commercial_model || '__NONE__'} onValueChange={v => setForm(f => ({ ...f, commercial_model: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select</SelectItem>{COMMERCIAL_MODELS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Capabilities</Label>
                  <div className="flex flex-wrap gap-1.5">{CAPABILITIES.map(cap => (
                    <button key={cap} onClick={() => toggleCap(cap)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${form.capabilities.includes(cap) ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' : 'bg-white/5 text-zinc-500 border-white/10 hover:border-white/20'}`}>{cap}</button>
                  ))}</div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSave} className="bg-cyan-500 hover:bg-cyan-600 text-black flex-1" data-testid="partner-form-save">{editingId ? 'Update Partner' : 'Create Partner'}</Button>
                  <Button onClick={closeForm} variant="outline" className="border-white/10">Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Modal */}
          {deleteTarget && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeDelete}>
              <div className="bg-[#121214] rounded-lg border border-red-500/20 w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="delete-partner-modal">
                {deleteUsage?.is_system_partner ? (
                  <>
                    <div className="flex items-center gap-2 text-red-400"><ShieldCheck size={20} /> <span className="font-medium">System Partner Protected</span></div>
                    <p className="text-sm text-zinc-400">"{deleteTarget.partner_name}" is a system partner and cannot be deleted.</p>
                    <Button onClick={closeDelete} variant="outline" className="w-full border-white/10">Close</Button>
                  </>
                ) : deleteUsage === null ? (
                  <div className="text-center text-zinc-500 py-4">Checking usage...</div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-red-400"><Warning size={20} weight="fill" /> <span className="font-medium">Delete "{deleteTarget.partner_name}"</span></div>

                    {deleteUsage.deletable ? (
                      <p className="text-sm text-zinc-400">This partner has no transaction references and can be safely deleted. All associated mapping rules will also be removed.</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-sm">
                          <p className="text-red-300 font-medium">This partner is referenced in existing data:</p>
                          <div className="mt-2 text-xs text-red-300/80 space-y-1">
                            <div>{deleteUsage.transaction_count} transactions</div>
                            <div>{deleteUsage.item_count} item records</div>
                            <div>{deleteUsage.mapping_count} mapping rules</div>
                          </div>
                          <p className="mt-2 text-red-300/70 text-xs">Deleting will affect historical analytics. Consider deactivating instead.</p>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-zinc-400">Reason for deletion</Label>
                          <Input value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Why are you deleting?" className="bg-[#18181B] border-white/10 text-xs" data-testid="delete-reason-input" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-red-400">Type DELETE to confirm</Label>
                          <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="DELETE" className="bg-[#18181B] border-red-500/30 text-xs" data-testid="delete-confirm-input" />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      {!deleteUsage.deletable && (
                        <Button onClick={() => { partnersApi.update(deleteTarget.partner_id, { status: 'inactive' }).then(() => { toast.success('Partner deactivated'); closeDelete(); fetchAll(); }); }} variant="outline" className="flex-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="deactivate-partner-btn">
                          Deactivate Instead
                        </Button>
                      )}
                      <Button onClick={handleDelete} disabled={deleteLoading || (!deleteUsage.deletable && deleteConfirmText !== 'DELETE')} className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40" data-testid="confirm-delete-btn">
                        {deleteLoading ? 'Deleting...' : 'Delete Partner'}
                      </Button>
                      <Button onClick={closeDelete} variant="outline" className="border-white/10">Cancel</Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MAPPINGS TAB ── */}
      {tab === 'mappings' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Select value={filterPartner || '__ALL__'} onValueChange={v => setFilterPartner(v === '__ALL__' ? '' : v)}>
                <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[180px]" data-testid="mapping-filter-partner"><SelectValue placeholder="All partners" /></SelectTrigger>
                <SelectContent><SelectItem value="__ALL__">All partners</SelectItem>{partners.map(p => <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-zinc-500 font-mono">{filteredMappings.length} rules</span>
            </div>
            <Button onClick={openMapCreate} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-black gap-1.5" data-testid="add-mapping-btn"><Plus size={14} /> Add Rule</Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="mappings-table">
              <thead><tr className="bg-zinc-800/50 text-zinc-400 text-left">
                <th className="p-3 font-mono">Source Value</th><th className="p-3 font-mono">Source Field</th><th className="p-3 font-mono">Type</th><th className="p-3 font-mono">Mapped Partner</th><th className="p-3 font-mono text-right">Confidence</th><th className="p-3 font-mono text-right">Priority</th>
              </tr></thead>
              <tbody>{filteredMappings.map((m, idx) => {
                const partner = partners.find(p => p.partner_id === m.mapped_partner_id);
                return (
                  <tr key={`${m.source_value}-${m.mapped_partner_id}-${idx}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 font-mono text-zinc-200">{m.source_value}</td>
                    <td className="p-3 text-zinc-400">{m.source_field || 'any'}</td>
                    <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${m.mapping_type === 'exact' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-violet-500/10 text-violet-400'}`}>{m.mapping_type}</span></td>
                    <td className="p-3 font-mono text-cyan-400">{partner?.partner_name || m.mapped_partner_id}</td>
                    <td className="p-3 font-mono text-right text-zinc-300">{(m.confidence * 100).toFixed(0)}%</td>
                    <td className="p-3 font-mono text-right text-zinc-400">{m.priority}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>

          {showMapForm && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeMapForm}>
              <div className="bg-[#121214] rounded-lg border border-white/10 w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="mapping-form-modal">
                <h3 className="text-lg font-medium">New Mapping Rule</h3>
                <div className="space-y-1.5"><Label className="text-xs">Source Value</Label><Input value={mapForm.source_value} onChange={e => setMapForm(f => ({ ...f, source_value: e.target.value }))} placeholder="e.g. zomato_dining" className="bg-[#18181B] border-white/10 text-xs" data-testid="mapping-form-source" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Source Field</Label><Select value={mapForm.source_field || '__ANY__'} onValueChange={v => setMapForm(f => ({ ...f, source_field: v === '__ANY__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent>{SOURCE_FIELDS.map(sf => <SelectItem key={sf.value || '__ANY__'} value={sf.value || '__ANY__'}>{sf.label}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5"><Label className="text-xs">Match Type</Label><Select value={mapForm.mapping_type} onValueChange={v => setMapForm(f => ({ ...f, mapping_type: v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="exact">Exact</SelectItem><SelectItem value="regex">Regex</SelectItem></SelectContent></Select></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Map to Partner</Label><Select value={mapForm.mapped_partner_id || '__NONE__'} onValueChange={v => setMapForm(f => ({ ...f, mapped_partner_id: v === '__NONE__' ? '' : v }))}><SelectTrigger className="bg-[#18181B] border-white/10 text-xs" data-testid="mapping-form-partner"><SelectValue placeholder="Select partner" /></SelectTrigger><SelectContent><SelectItem value="__NONE__">Select partner</SelectItem>{partners.filter(p => p.status === 'active').map(p => <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Confidence (0-1)</Label><Input type="number" min="0" max="1" step="0.05" value={mapForm.confidence} onChange={e => setMapForm(f => ({ ...f, confidence: parseFloat(e.target.value) || 0 }))} className="bg-[#18181B] border-white/10 text-xs" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Priority</Label><Input type="number" min="1" max="999" value={mapForm.priority} onChange={e => setMapForm(f => ({ ...f, priority: parseInt(e.target.value) || 30 }))} className="bg-[#18181B] border-white/10 text-xs" /></div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleMapSave} className="bg-cyan-500 hover:bg-cyan-600 text-black flex-1" data-testid="mapping-form-save">Create Rule</Button>
                  <Button onClick={closeMapForm} variant="outline" className="border-white/10">Cancel</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ANALYTICS TAB ── */}
      {tab === 'analytics' && (
        <div className="space-y-4" data-testid="partner-analytics-tab">
          {analyticsLoading ? (
            <div className="py-16 text-center text-zinc-500">Loading analytics...</div>
          ) : !summary?.partners?.length ? (
            <div className="surface-base rounded-lg py-16 text-center text-zinc-500">
              <ChartBar size={48} className="mx-auto text-zinc-700 mb-4" />
              <p>No partner analytics data yet. Upload transactions to see insights.</p>
            </div>
          ) : (
            <>
              {/* Partner Performance Table */}
              <div className="surface-base rounded-lg p-5">
                <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2"><ChartBar size={16} className="text-cyan-400" /> Partner Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="analytics-partner-table">
                    <thead><tr className="bg-zinc-800/50 text-zinc-400 text-left">
                      <th className="p-3 font-mono">Partner</th><th className="p-3 font-mono">Type</th><th className="p-3 font-mono">Channel</th>
                      <th className="p-3 font-mono text-right">Orders</th><th className="p-3 font-mono text-right">Gross Sales</th>
                      <th className="p-3 font-mono text-right">Discounts</th><th className="p-3 font-mono text-right">Net Sales</th>
                      <th className="p-3 font-mono text-right">AOV</th><th className="p-3 font-mono text-right">Contribution</th>
                    </tr></thead>
                    <tbody>{summary.partners.map(p => {
                      const meta = partners.find(pm => pm.partner_id === p.partner_id);
                      return (
                        <tr key={p.partner_id} className="border-t border-white/5" data-testid={`analytics-row-${p.partner_id}`}>
                          <td className="p-3 font-mono text-zinc-200">{meta?.partner_name || p.partner_id}</td>
                          <td className="p-3 text-zinc-500">{meta?.partner_type || '-'}</td>
                          <td className="p-3 text-zinc-500">{meta?.channel || '-'}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{p.orders}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(p.gross_sales)}</td>
                          <td className="p-3 font-mono text-right text-red-400/70">{p.discounts > 0 ? `-${fmtCurrency(p.discounts)}` : '0'}</td>
                          <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(p.net_sales)}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(p.avg_order_value)}</td>
                          <td className="p-3 font-mono text-right text-[#D4FF00]">{p.contribution_pct}%</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              </div>

              {/* Revenue Comparison Chart */}
              <div className="surface-base rounded-lg p-5">
                <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2"><ChartBar size={16} className="text-cyan-400" /> Revenue Comparison</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary.partners} layout="vertical" barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis type="number" stroke="#71717A" fontSize={11} tickFormatter={v => fmtCurrency(v)} />
                      <YAxis dataKey="partner_id" type="category" stroke="#71717A" fontSize={10} width={120} tickFormatter={v => { const m = partners.find(p => p.partner_id === v); return m?.partner_name || v; }} />
                      <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={v => [fmtCurrency(v)]} />
                      <Bar dataKey="net_sales" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Trend Lines */}
              {trendChartData.length > 0 && (
                <div className="surface-base rounded-lg p-5">
                  <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2"><TrendUp size={16} className="text-emerald-400" /> Revenue Trends by Partner</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                        <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                        <YAxis stroke="#71717A" fontSize={11} tickFormatter={v => fmtCurrency(v)} />
                        <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={v => [fmtCurrency(v)]} />
                        <Legend />
                        {trendPartnerIds.map((pid, i) => {
                          const meta = partners.find(p => p.partner_id === pid);
                          return <Line key={pid} type="monotone" dataKey={pid} name={meta?.partner_name || pid} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />;
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Mapping Coverage */}
              <div className="surface-base rounded-lg p-5">
                <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2"><Plugs size={16} className="text-violet-400" /> Mapping Coverage</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {partners.filter(p => p.partner_id !== 'unknown').map(p => {
                    const ruleCount = mappings.filter(m => m.mapped_partner_id === p.partner_id).length;
                    const hasData = summary.partners.some(sp => sp.partner_id === p.partner_id);
                    return (
                      <div key={p.partner_id} className="bg-zinc-800/30 rounded-md p-3 text-xs">
                        <div className="font-mono text-zinc-200 mb-1">{p.partner_name}</div>
                        <div className="flex justify-between text-zinc-500"><span>Rules</span><span className="font-mono text-zinc-300">{ruleCount}</span></div>
                        <div className="flex justify-between text-zinc-500"><span>Has data</span><span className={hasData ? 'text-emerald-400' : 'text-zinc-600'}>{hasData ? 'Yes' : 'No'}</span></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerAdminPage;
